import { z } from 'zod';

export const USER_ROLES = ['platform_access', 'central_access'] as const;
export const USER_ACCOUNT_STATUSES = ['pending_activation', 'active', 'suspended', 'removed'] as const;

/** Client kinds: the Console keeps the refresh token in a cookie, the Extension in its session storage (technical §15.3). */
export const AUTH_CLIENTS = ['console', 'extension'] as const;

export const accountDto = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(USER_ROLES),
  projectScope: z.array(z.string().uuid()),
  status: z.enum(USER_ACCOUNT_STATUSES),
  createdAt: z.string().datetime(),
});
export type AccountDto = z.infer<typeof accountDto>;

export const currentAccountDto = accountDto.extend({
  /** The platform inactivity setting, so the Console closes the session at the same moment as the API. */
  sessionIdleMinutes: z.number().int(),
});
export type CurrentAccountDto = z.infer<typeof currentAccountDto>;

export const signInRequestDto = z
  .object({
    email: z.string().min(1).max(320),
    password: z.string().min(1).max(1024),
    client: z.enum(AUTH_CLIENTS).default('console'),
  })
  .strict();
export type SignInRequest = z.infer<typeof signInRequestDto>;

/** For the Extension only; the Console sends the cookie and leaves the body empty. */
export const refreshRequestDto = z.object({ refreshToken: z.string().min(1).max(256).optional() }).strict();

export const sessionDto = z.object({
  accessToken: z.string(),
  expiresInSeconds: z.number().int(),
  /** Present for the Extension only. */
  refreshToken: z.string().optional(),
  account: currentAccountDto,
});
export type SessionDto = z.infer<typeof sessionDto>;

export const changePasswordRequestDto = z
  .object({ currentPassword: z.string().min(1).max(1024), newPassword: z.string().min(1).max(1024) })
  .strict();

export const setPasswordWithLinkRequestDto = z
  .object({ token: z.string().min(1).max(256), password: z.string().min(1).max(1024) })
  .strict();

export const createAccountRequestDto = z
  .object({
    email: z.string().min(3).max(320),
    displayName: z.string().min(1).max(120),
    role: z.enum(USER_ROLES),
    projectScope: z.array(z.string().uuid()).max(200).default([]),
  })
  .strict();
export type CreateAccountRequest = z.infer<typeof createAccountRequestDto>;

export const updateAccountScopeRequestDto = z
  .object({ projectScope: z.array(z.string().uuid()).max(200) })
  .strict();

/**
 * A link to hand over: the activation link of a new account, or a reset link.
 * `url` is present only when no e-mail carried it (phase 7 note, decision 1),
 * and is shown once.
 */
export const accountLinkDto = z.object({
  purpose: z.enum(['activation', 'password_reset']),
  delivery: z.enum(['sent', 'hand_over']),
  url: z.string().url().optional(),
  expiresAt: z.string().datetime(),
});
export type AccountLinkDto = z.infer<typeof accountLinkDto>;

export const createdAccountDto = z.object({ account: accountDto, link: accountLinkDto });
export type CreatedAccountDto = z.infer<typeof createdAccountDto>;

export const platformSettingsDto = z.object({ sessionIdleMinutes: z.number().int() });
export type PlatformSettingsDto = z.infer<typeof platformSettingsDto>;

export const updatePlatformSettingsRequestDto = z.object({ sessionIdleMinutes: z.number().int() }).strict();

/** The rights matrix as the API applies it (functional §9.3), read-only. */
export const rolePermissionsDto = z.object({
  permissions: z.array(
    z.object({
      key: z.string(),
      roles: z.array(z.enum(USER_ROLES)),
    }),
  ),
});
export type RolePermissionsDto = z.infer<typeof rolePermissionsDto>;
